import React, { useEffect, useMemo } from 'react';
import { Typography, Tag, Button, Popconfirm, message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { CopyOutlined } from '@ant-design/icons';
import CrudPage from '../../../components/common/CrudPage';
import UploadImage from '../../../components/UploadImage';
import { serviceBlockService } from '../../../services/next';
import { listAllActiveServicesRequest } from '../../../services/cms/serviceServiceRequest';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { label: 'Duyệt', value: 1 },
  { label: 'Chưa duyệt', value: 0 },
];

// Layout types (mirror the old CMS "Loại layout" select)
const TYPE_OPTIONS = [
  { label: 'Grid', value: 'grid' },
  { label: 'List Dọc', value: 'list_vertical' },
  { label: 'List Ngang', value: 'list_horizontal' },
  { label: 'Grid Icon', value: 'gridicon' },
  { label: 'Image', value: 'image' },
  { label: 'Tử vi', value: 'tuvi' },
];

const IS_SHOW_PRO_OPTIONS = [
  { label: 'Ẩn', value: 0 },
  { label: 'Hiện', value: 1 },
];

const QuanLyKhoiDichVu = () => {
  const { data: metaRes, isError: isMetaError } = useQuery({
    queryKey: ['serviceBlockMetadata'],
    queryFn: serviceBlockService.getMetadata,
    staleTime: 5 * 60 * 1000,
  });

  // Metadata cấp danh sách Vị trí và Target. API này cần quyền riêng ("Metadata"): role không
  // có quyền sẽ nhận 403 và hai dropdown rỗng im lặng — nay Vị trí là bắt buộc nên phải nói rõ
  // lý do, nếu không người dùng không hiểu vì sao không lưu được (QA-08).
  useEffect(() => {
    if (isMetaError) {
      message.warning(
        'Không tải được danh sách Vị trí / Target. Tài khoản có thể thiếu quyền "Metadata" của màn Khối dịch vụ — liên hệ quản trị để được cấp.',
        6
      );
    }
  }, [isMetaError]);

  // Nhóm dịch vụ: dùng chung API service với trang "Danh sách gói"
  const { data: activeServices } = useQuery({
    queryKey: ['allActiveServicesForServiceBlock'],
    queryFn: () => listAllActiveServicesRequest({}),
    staleTime: 5 * 60 * 1000,
  });

  const handleCopy = async (id, actionRef) => {
    try {
      const res = await serviceBlockService.copy(id);
      if (res && res.code === 200) {
        message.success('Sao chép thành công');
        if (actionRef && actionRef.current) {
          actionRef.current.reload();
        }
      } else {
        message.error(res?.message || 'Sao chép thất bại');
      }
    } catch (err) {
      message.error('Lỗi sao chép');
    }
  };

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
      { title: 'TIÊU ĐỀ', dataIndex: 'title', key: 'title', width: 200, render: (t) => <Text strong>{t}</Text> },
      { title: 'MÔ TẢ', dataIndex: 'description', key: 'description', width: 240, ellipsis: true },
      { title: 'VERSION', dataIndex: 'versionText', key: 'versionText', width: 120 },
      { title: 'TẬP KHÁCH HÀNG', dataIndex: 'targetsText', key: 'targetsText', width: 220, ellipsis: true },
      {
        title: 'HIỂN THỊ',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (s) => {
          const map = { 0: ['default', 'CHƯA DUYỆT'], 1: ['green', 'DUYỆT'] };
          const [c, l] = map[s] || ['default', s];
          return <Tag color={c}>{l}</Tag>;
        },
      },
    ],
    []
  );

  const formFields = useMemo(() => {
    const meta = metaRes?.data || {};
    const positionOptions = meta.positions || [];
    // group_id map theo danh sách dịch vụ mới (giống trang "Danh sách gói"): label = tên, value = id
    const serviceOptions = (activeServices || []).map((s) => ({ value: String(s.id), label: s.name }));
    const topicOptions = meta.topics || { platform: [], version: [], topic: [], topic_user_id: [] };

    return [
      { name: 'title', label: 'Tiêu đề', type: 'input', rules: [{ required: true, message: 'Vui lòng nhập tiêu đề!' }], span: 24 },
      { name: 'image', label: 'Ảnh tiêu đề', type: 'custom', span: 24, render: ({ value, onChange }) => <UploadImage value={value} onChange={onChange} /> },
      { name: 'description', label: 'Mô tả', type: 'textarea', rows: 3, span: 24 },
      // Loại layout / Nhóm dịch vụ / Vị trí: bắt buộc theo yêu cầu nghiệp vụ (QA-07),
      // đã thêm required() vào serviceBlockValidator ở API trong cùng đợt này.
      { name: 'type', label: 'Loại layout', type: 'select', options: TYPE_OPTIONS, span: 12, rules: [{ required: true, message: 'Vui lòng chọn loại layout!' }] },
      { name: 'grid_custom', label: 'Tỉ lệ Grid (VD: 3_3)', type: 'input', span: 12, propsInput: { placeholder: 'Chỉ dùng cho Grid / Grid Icon' } },
      { name: 'group_id', label: 'Nhóm dịch vụ', type: 'select', options: serviceOptions, span: 12, rules: [{ required: true, message: 'Vui lòng chọn nhóm dịch vụ!' }], propsInput: { showSearch: true, allowClear: true, optionFilterProp: 'label' } },
      { name: 'position', label: 'Vị trí', type: 'select', options: positionOptions, span: 12, rules: [{ required: true, message: 'Vui lòng chọn vị trí!' }], propsInput: { showSearch: true, allowClear: true, optionFilterProp: 'label' } },
      { name: 'deeplink', label: 'Deeplink', type: 'input', span: 12 },
      // InputNumber thay cho <input type="number"> HTML (thẻ đó vẫn nhận 'e', '+', '-')
      { name: 'order_place', label: 'Thứ tự xuất hiện', type: 'number', span: 12, propsInput: { precision: 0 } },

      { type: 'divider', label: 'Đối tượng hiển thị (Target)' },
      { name: 'platform', label: 'Platform', type: 'select', options: topicOptions.platform, span: 12, propsInput: { mode: 'multiple', allowClear: true, placeholder: 'Tất cả', optionFilterProp: 'label' } },
      { name: 'version', label: 'Version', type: 'select', options: topicOptions.version, span: 12, propsInput: { mode: 'tags', allowClear: true, placeholder: 'Tất cả (có thể nhập v9.0:v9.3 ...)', optionFilterProp: 'label' } },
      { name: 'topic', label: 'Topic', type: 'select', options: topicOptions.topic, span: 12, propsInput: { mode: 'multiple', allowClear: true, placeholder: 'Tất cả', optionFilterProp: 'label' } },
      { name: 'topic_user_id', label: 'Topic User ID', type: 'select', options: topicOptions.topic_user_id, span: 12, propsInput: { mode: 'tags', allowClear: true, placeholder: 'Chỉ định khách hàng', optionFilterProp: 'label' } },

      { type: 'divider', label: 'Danh sách dịch vụ trong khối' },
      {
        name: 'items',
        type: 'group',
        isSort: true,
        titleButtonAdd: 'Thêm dịch vụ',
        minItem: 0,
        filedsList: [
          { name: 'id', type: 'input', propsFormItem: { hidden: true } },
          { name: 'thumb', label: 'Hình ảnh', type: 'custom', span: 24, render: ({ value, onChange }) => <UploadImage value={value} onChange={onChange} /> },
          // Tên / Thứ tự / Nội dung của từng dịch vụ con: bắt buộc theo nghiệp vụ (QA-07).
          // RenderForm hỗ trợ rules cho từng dòng trong nhóm nên mỗi dòng báo lỗi riêng.
          { name: 'title', label: 'Tên dịch vụ', type: 'input', span: 12, rules: [{ required: true, message: 'Vui lòng nhập tên dịch vụ!' }] },
          { name: 'order_place', label: 'Thứ tự', type: 'number', span: 12, propsInput: { precision: 0 }, rules: [{ required: true, message: 'Vui lòng nhập thứ tự!' }] },
          { name: 'link_free', label: 'Deeplink Free', type: 'input', span: 12 },
          { name: 'link_pro', label: 'Deeplink Pro', type: 'input', span: 12 },
          { name: 'link_web', label: 'Link Web', type: 'input', span: 12 },
          { name: 'is_show_pro', label: 'Hiển thị khi pro', type: 'select', options: IS_SHOW_PRO_OPTIONS, span: 12 },
          { name: 'content', label: 'Nội dung', type: 'textarea', rows: 3, span: 24, rules: [{ required: true, message: 'Vui lòng nhập nội dung!' }] },
          { name: 'metadata', label: 'Metadata (Html)', type: 'textarea', rows: 3, span: 24 },
        ],
      },

      { type: 'divider', label: 'Trạng thái' },
      { name: 'status', label: 'Trạng thái', type: 'select', options: STATUS_OPTIONS, defaultValue: 0, span: 12 },
    ];
  }, [metaRes, activeServices]);

  const filterFields = useMemo(
    () => [
      {
        label: 'Trạng thái',
        value: 'status',
        options: [{ label: 'Tất cả', value: '' }, ...STATUS_OPTIONS],
      },
    ],
    []
  );

  const searchFields = useMemo(
    () => [
      { label: 'Tiêu đề', value: 'title' },
      { label: 'Mô tả', value: 'description' },
    ],
    []
  );

  // The list row already carries split type, grouped targets and items,
  // so populate the edit/copy form directly from it.
  const mapDataToForm = (record) => ({
    type: record.type_base || record.type || '',
    grid_custom: record.grid_custom || '',
    group_id: record.group_id != null && record.group_id !== '' ? String(record.group_id) : undefined,
    platform: record.platform || [],
    version: record.version || [],
    topic: record.topic || [],
    topic_user_id: record.topic_user_id || [],
    items: (record.items || []).map((it) => ({
      id: it.id,
      thumb: it.thumb,
      title: it.title,
      content: it.content,
      metadata: it.metadata,
      order_place: it.order_place,
      link_free: it.link_free,
      link_pro: it.link_pro,
      link_web: it.link_web,
      is_show_pro: it.is_show_pro,
    })),
  });

  const customActions = (record, actionRef) => (
    <Popconfirm
      title="Sao chép khối dịch vụ này?"
      onConfirm={() => handleCopy(record.id, actionRef)}
      okText="Có"
      cancelText="Không"
    >
      <Button type="default" icon={<CopyOutlined />} size="small">
        Copy
      </Button>
    </Popconfirm>
  );

  return (
    <CrudPage
      title="QUẢN LÝ KHỐI DỊCH VỤ"
      subTitle="Quản lý các khối dịch vụ hiển thị trên ứng dụng"
      itemsName="Khối dịch vụ"
      queryKey="serviceBlockList"
      service={serviceBlockService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchFields={searchFields}
      searchPlaceholder="Tìm kiếm theo tiêu đề..."
      addButtonLabel="Thêm khối dịch vụ"
      formConfig={{ width: 900 }}
      customActions={customActions}
      mapDataToForm={mapDataToForm}
    />
  );
};

export default QuanLyKhoiDichVu;
